/**
 * StudioHome — 크리에이티브 스튜디오 홈
 *
 * 테이블/차트 없이 카드 기반 비주얼 레이아웃.
 * - 인사 영역 (아바타 + 이름 + 크레딧 pill)
 * - 내 청첩장 카드 가로 스크롤
 * - 최근 AI 사진 가로 스크롤
 */

import { useNavigate } from 'react-router'
import { Heart, Camera, Plus, Eye } from 'lucide-react'
import { useAuth } from '../../lib/auth.js'
import { useApiFetch } from '../../hooks/useApiFetch.js'
import { Avatar, Badge, Button, Spinner, EmptyState } from '../../components/ui/index.js'
import type { InvitationStatus } from '@cugumi/shared/schemas'
import styles from './StudioHome.module.css'

interface DashboardStats {
  invitationCount: number
  totalViews: number
  rsvpCount: number
}

interface InvitationSummary {
  id: string
  groomName: string
  brideName: string
  status: InvitationStatus
  viewCount: number
  weddingDate: string
  galleryImages: string[] | null
  aiPhotoUrl?: string | null
  data?: {
    groom?: { name?: string }
    bride?: { name?: string }
    gallery?: { coverImage?: string; images?: string[] }
    aiPhotoUrl?: string
  } | null
  extendedData?: { gallery?: { coverImage?: string } } | null
  createdAt: string
}

interface InvitationsResponse {
  invitations: InvitationSummary[]
  pagination: { total: number; page: number; pageSize: number; totalPages: number; hasNext: boolean; hasPrev: boolean }
}

interface Generation {
  id: string
  generatedUrls: string[] | null
  style: string | null
  createdAt: string
}

interface GenerationsResponse {
  generations: Generation[]
  pagination: { total: number; page: number; pageSize: number; totalPages: number; hasNext: boolean; hasPrev: boolean }
}

const STATUS_CONFIG: Partial<Record<InvitationStatus, { label: string; color: 'gray' | 'green' | 'yellow' }>> = {
  DRAFT: { label: '작성중', color: 'gray' },
  PUBLISHED: { label: '공개됨', color: 'green' },
  EXPIRED: { label: '만료됨', color: 'yellow' },
}

export function StudioHome() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const { data: stats } = useApiFetch<DashboardStats>('/api/dashboard/stats')
  const { data: invData, isLoading: invLoading, error: invError } = useApiFetch<InvitationsResponse>('/api/invitations')
  const { data: genData, isLoading: genLoading, error: genError } = useApiFetch<GenerationsResponse>('/api/ai/generations?pageSize=8')

  const displayName = user?.name ?? user?.email?.split('@')[0] ?? ''
  const activeInvitations = invData?.invitations ?? []

  const recentPhotos = (genData?.generations ?? []).flatMap(g =>
    (g.generatedUrls ?? []).map(url => ({ url, id: g.id, style: g.style }))
  )

  return (
    <div className={styles.root}>
      {/* 인사 영역 */}
      <section className={styles.greeting}>
        <div className={styles.greetingLeft}>
          <Avatar
            src={user?.image ?? undefined}
            name={displayName || '?'}
            size="lg"
          />
          <div className={styles.greetingText}>
            <h1 className={styles.greetingTitle}>
              {displayName}님, 안녕하세요
            </h1>
            <p className={styles.greetingSubtitle}>
              오늘도 멋진 웨딩을 준비해볼까요?
            </p>
          </div>
        </div>
        <Badge color="blue" className={styles.creditBadge}>
          AI 크레딧 {user?.aiCredits ?? 0}개
        </Badge>
      </section>

      {/* 내 청첩장 */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>내 청첩장</h2>
          {activeInvitations.length > 0 && (
            <button
              className={styles.seeAllBtn}
              onClick={() => navigate('/studio/invitations')}
            >
              모두 보기
            </button>
          )}
        </div>

        {invLoading ? (
          <div className={styles.loadingRow}>
            <Spinner size="md" />
          </div>
        ) : invError ? (
          <EmptyState
            title="청첩장을 불러오지 못했어요"
            description="잠시 후 다시 시도해주세요"
          />
        ) : activeInvitations.length === 0 ? (
          <EmptyState
            icon={<Heart size={32} strokeWidth={1.5} />}
            title="아직 청첩장이 없어요"
            description="AI로 멋진 웨딩 사진을 만들고, 청첩장을 시작해보세요"
            action={
              <Button className={styles.ctaPill} onClick={() => navigate('/studio/ai')}>
                AI 웨딩 사진 만들기
              </Button>
            }
          />
        ) : (
          <div className={styles.invitationScroll}>
            {/* 새 청첩장 카드 */}
            <button
              className={styles.createCard}
              onClick={() => navigate('/studio/invitations')}
            >
              <Plus size={28} strokeWidth={1.8} />
              <span>새 청첩장</span>
            </button>

            {activeInvitations.slice(0, 5).map(inv => {
              const cover = inv.data?.gallery?.coverImage ?? inv.extendedData?.gallery?.coverImage ?? inv.data?.aiPhotoUrl ?? inv.aiPhotoUrl ?? inv.data?.gallery?.images?.[0] ?? inv.galleryImages?.[0]
              const status = STATUS_CONFIG[inv.status]
              return (
                <button
                  key={inv.id}
                  className={styles.invitationCard}
                  onClick={() => navigate(`/studio/editor/${inv.id}`)}
                >
                  <div className={styles.cardCover}>
                    {cover ? (
                      <img src={cover} alt="" className={styles.coverImg} />
                    ) : (
                      <div className={styles.coverPlaceholder}>
                        <Heart size={32} strokeWidth={1.5} />
                      </div>
                    )}
                    {status && (
                      <Badge color={status.color} className={styles.statusBadge}>
                        {status.label}
                      </Badge>
                    )}
                  </div>
                  <div className={styles.cardInfo}>
                    <span className={styles.cardNames}>
                      {inv.data?.groom?.name || inv.groomName} & {inv.data?.bride?.name || inv.brideName}
                    </span>
                    <span className={styles.cardMeta}>
                      <Eye size={14} strokeWidth={2} /> {inv.viewCount}
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </section>

      {/* 최근 AI 사진 */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>최근 AI 사진</h2>
          {recentPhotos.length > 0 && (
            <button
              className={styles.seeAllBtn}
              onClick={() => navigate('/studio/ai')}
            >
              모두 보기
            </button>
          )}
        </div>

        {genLoading ? (
          <div className={styles.loadingRow}>
            <Spinner size="md" />
          </div>
        ) : genError ? (
          <EmptyState
            title="사진을 불러오지 못했어요"
            description="잠시 후 다시 시도해주세요"
          />
        ) : recentPhotos.length === 0 ? (
          <EmptyState
            icon={<Camera size={32} strokeWidth={1.5} />}
            title="아직 생성된 사진이 없어요"
            description="두 분의 사진을 업로드하고, AI가 만드는 웨딩 사진을 만나보세요"
            action={
              <Button className={styles.ctaPill} onClick={() => navigate('/studio/ai')}>
                첫 AI 사진 만들기
              </Button>
            }
          />
        ) : (
          <div className={styles.photoScroll}>
            {recentPhotos.slice(0, 12).map(photo => (
              <button
                key={photo.url}
                className={styles.photoCard}
                onClick={() => navigate('/studio/ai')}
              >
                <img src={photo.url} alt="" className={styles.photoImg} />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* 퀵 스탯 (데이터 있을 때만) */}
      {stats && (stats.totalViews > 0 || stats.rsvpCount > 0) && (
        <section className={styles.quickStats}>
          <div className={styles.quickStatItem}>
            <span className={styles.quickStatValue}>{stats.totalViews}</span>
            <span className={styles.quickStatLabel}>총 조회수</span>
          </div>
          <div className={styles.quickStatItem}>
            <span className={styles.quickStatValue}>{stats.rsvpCount}</span>
            <span className={styles.quickStatLabel}>참석 응답</span>
          </div>
          <div className={styles.quickStatItem}>
            <span className={styles.quickStatValue}>{stats.invitationCount}</span>
            <span className={styles.quickStatLabel}>청첩장</span>
          </div>
        </section>
      )}
    </div>
  )
}
